/**
 * ReadOnlyFallback — structural summary panel for VQE-RO render modes that
 * have no structured renderer in this PRD. PRD: `Ralph/VisualQueryEditorReadOnly/PRD.md`
 * § "Fallback — Read-only Visualization".
 *
 * One card, one message: the title + hint live at the top of the same card as
 * the statement facts and the actions. (It used to be a separate hint box
 * above the card, under a readiness banner that repeated it — three copies of
 * "this is read-only" on one screen.) The workspace hides its read-only
 * banner while this panel is shown.
 */

import { useMemo } from 'react';

import { Button } from '../../../../../components/ui/button';
import { useAnalytics } from '../../../../../core/analytics/useAnalytics';
import type { QualifiedName, SqlEngine, StatementInfo } from '../../../../sql-grammar/model';
import { buildDdlCardModel } from '../../compact/compactDdlCard';
import { openDesignTableFromDdlCard } from '../../compact/openDesignTableFromDdlCard';
import { GrammarHarnessReportButton } from '../../harness/GrammarHarnessReportButton';
import type { ReportGrammarIssueDeps } from '../../harness/reportGrammarIssue';
import { SqlPreviewCode } from '../common/SqlPreviewCode';
import { setOperatorChoicesForEngine } from '../select/setOperatorKindMaps';
import { vqeCopy } from '../../ux/vqeCopy';
import { FullDdlCard } from './FullDdlCard';
import { StatementTextDisclosure } from './StatementTextDisclosure';
import { STATEMENT_KIND_LABELS } from './statementKindLabels';
import { FULL_CANVAS_ROOT_CLASS } from '../common/canvasLayout';

export type ReadOnlyFallbackReason =
  | 'unsupported-ddl'
  | 'unsupported-procedural'
  | 'unsupported-set-precedence'
  | 'unsupported-graph-table'
  | 'unsupported-unknown';

export type ReadOnlyFallbackProps = {
  statement: StatementInfo;
  reason: ReadOnlyFallbackReason;
  bufferText: string;
  onEditAsSql: () => void;
  dialect?: string;
  reportApi?: ReportGrammarIssueDeps;
  /** Gates the DDL fact card's Design-table hand-off (EDG-06..10). */
  connectionId?: string | null;
  /** Active database, forwarded to the Design-table hand-off. */
  database?: string | null;
};

// EDG-22/EDG-24: `unsupported-set-precedence` is a KNOWN, correct downgrade —
// `detectSetOperatorPrecedence` intentionally stops flattening genuinely
// ambiguous multi-level UNION/INTERSECT/EXCEPT precedence — not a parser bug,
// so it must never show "Report rendering issue" as if something broke. Only
// a genuinely-unrecognized statement is worth a bug report.
const REPORTABLE_REASONS: ReadonlySet<ReadOnlyFallbackReason> = new Set(['unsupported-unknown']);

const FALLBACK_TITLES: Record<ReadOnlyFallbackReason, string> = {
  'unsupported-ddl': 'DDL statement — read-only',
  'unsupported-procedural': 'Procedural body — read-only',
  'unsupported-set-precedence': 'Complex set-operator query — read-only',
  'unsupported-graph-table': 'Graph query (GRAPH_TABLE) — read-only',
  'unsupported-unknown': 'Unrecognized statement — read-only',
};

/**
 * Teaching hints for the reasons that never need the engine — each names the
 * construct, why it can't be edited here, and the real next step (SQL editor,
 * or Table Designer for structural table work). `unsupported-set-precedence`
 * is engine-dependent (BUG 2) and computed separately by {@link fallbackHint}.
 */
const FALLBACK_HINTS: Record<Exclude<ReadOnlyFallbackReason, 'unsupported-set-precedence'>, string> = {
  'unsupported-ddl':
    'The Visual Query Editor does not edit DDL (CREATE, ALTER, DROP, GRANT, …). Open the statement in the SQL editor, or use the Table Designer for a visual table editor.',
  'unsupported-procedural':
    'This is a procedural block (stored-logic body) — its statements are edited as SQL here. Open the SQL editor to change them.',
  'unsupported-graph-table':
    'This query uses GRAPH_TABLE (SQL/PGQ graph pattern matching) — the visual editor can’t edit its MATCH pattern or COLUMNS projection yet. Open the SQL editor to keep working on it.',
  'unsupported-unknown':
    'This statement doesn’t parse as SQL the visual editor recognizes — open the SQL editor to keep working on it.',
};

const SQL_ENGINES: ReadonlySet<string> = new Set<SqlEngine>(['mssql', 'postgres', 'mysql', 'oracle', 'sqlite']);

function isSqlEngine(value: string | undefined): value is SqlEngine {
  return value !== undefined && SQL_ENGINES.has(value);
}

/**
 * Dialect-correct set-operator list for the `unsupported-set-precedence` hint
 * (BUG 2 full-mode fix). `dialect` is `ParseOptions.engine`, already forwarded
 * unconditionally by the statement body — no new prop needed. Oracle
 * spells its third operator MINUS (EXCEPT needs 21c+); every other engine
 * uses EXCEPT — see `setOperatorChoicesForEngine`. Falls back to the
 * universal (non-Oracle) list when the dialect isn't a known engine yet.
 */
function setPrecedenceOperatorList(dialect?: string): string {
  const engine: SqlEngine = isSqlEngine(dialect) ? dialect : 'postgres';
  const kinds = setOperatorChoicesForEngine(engine).map((choice) => choice.kind);
  return Array.from(new Set(kinds)).join(' / ');
}

function fallbackHint(reason: ReadOnlyFallbackReason, dialect?: string): string {
  if (reason === 'unsupported-set-precedence') {
    return `This query mixes ${setPrecedenceOperatorList(dialect)} with parentheses that override their normal precedence — the visual editor doesn’t restructure that shape yet. Open the SQL editor to change the grouping.`;
  }
  return FALLBACK_HINTS[reason];
}

function formatQualifiedName(q: QualifiedName): string {
  return q.raw && q.raw.length > 0 ? q.raw : q.name;
}

function statementBody(bufferText: string, statement: StatementInfo): string {
  const start = Math.max(0, statement.range.startOffset);
  const end = Math.max(start, Math.min(bufferText.length, statement.range.endOffset));
  return bufferText.slice(start, end);
}

export function ReadOnlyFallback({
  statement,
  reason,
  bufferText,
  onEditAsSql,
  dialect,
  reportApi,
  connectionId,
  database,
}: ReadOnlyFallbackProps) {
  const { track } = useAnalytics();
  const title = FALLBACK_TITLES[reason];
  const hint = fallbackHint(reason, dialect);
  const kindLabel = STATEMENT_KIND_LABELS[statement.kind] ?? statement.kind;
  const body = statementBody(bufferText, statement);
  const showReport = Boolean(dialect) && REPORTABLE_REASONS.has(reason);

  // EDG-06..10: for a recognized DDL statement, REUSE the compact popover's
  // pure, context-free DDL model to render the same rich fact card full mode
  // deserves (object name + parsed facts + Design-table hand-off) instead of
  // the fact-free generic card. Null model (unrecognized DDL, or no resolved
  // engine to parse with) → the honest generic card, no fabricated facts.
  const ddlModel = useMemo(
    () =>
      reason === 'unsupported-ddl' && isSqlEngine(dialect)
        ? buildDdlCardModel(body, { engine: dialect })
        : null,
    [reason, dialect, body],
  );

  const handleDesignTable = () => {
    const design = ddlModel?.designTable;
    if (!design || !connectionId) return;
    // Mirror the compact path (`CompactVqePopover`): same event + props, plus a
    // `source` distinguisher for the full-mode surface.
    track('feature:vqe_ddl_card_action', { action: 'design-table', objectKind: 'table', source: 'full' });
    openDesignTableFromDdlCard({ design, connectionId, database: database ?? null });
  };

  return (
    <div
      className={FULL_CANVAS_ROOT_CLASS}
      data-testid="vqe-readonly-fallback"
      data-reason={reason}
    >
      {ddlModel ? (
        <FullDdlCard
          model={ddlModel}
          body={body}
          onEditAsSql={onEditAsSql}
          onDesignTable={handleDesignTable}
          connectionId={connectionId}
          hint={vqeCopy.ddlCard.fullHint(ddlModel.designTable !== null)}
        />
      ) : (
        <div className="rounded-lg border border-border bg-card p-4 shadow-sm" data-testid="vqe-readonly-card">
          <h3 className="text-sm font-semibold text-foreground" data-testid="vqe-readonly-fallback-title">
            {title}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground" data-testid="vqe-readonly-fallback-hint">
            {hint}
          </p>

          {/* EDG-17/EDG-19 (copy half): this "Statement <KIND>" fact echoes
              parser vocabulary that's redundant with the card title for
              unsupported-procedural (`FALLBACK_TITLES['unsupported-procedural']`
              already says "Procedural body — read-only"). Every other reason
              keeps the fact — it's the only place the statement kind shows. */}
          {reason !== 'unsupported-procedural' ? (
            <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">Statement</span>
              <span
                className="font-mono text-sm font-medium text-foreground"
                data-testid="vqe-readonly-fallback-kind"
              >
                {kindLabel}
              </span>
            </div>
          ) : null}

          <ReferencedTables label="Reads from" tables={statement.readsFrom} testId="vqe-readonly-fallback-reads-from" />
          <ReferencedTables label="Writes to" tables={statement.writesTo} testId="vqe-readonly-fallback-writes-to" />

          <StatementTextDisclosure testIdPrefix="vqe-readonly-fallback">
            <SqlPreviewCode sql={body} />
          </StatementTextDisclosure>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            {showReport && dialect ? (
              <GrammarHarnessReportButton
                reason={reason === 'unsupported-unknown' ? 'unsupported-unknown' : 'unsupported-set-precedence'}
                bufferText={bufferText}
                dialect={dialect}
                statementId={statement.id}
                diagnostic={`ReadOnlyFallback: ${reason} (kind=${statement.kind})`}
                api={reportApi}
              />
            ) : null}
            <Button
              variant="default"
              size="sm"
              onClick={onEditAsSql}
              data-testid="vqe-readonly-fallback-edit-as-sql"
            >
              {vqeCopy.actions.editAsSql}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ReferencedTables({
  label,
  tables,
  testId,
}: {
  label: string;
  tables: readonly QualifiedName[];
  testId: string;
}) {
  if (tables.length === 0) return null;
  return (
    <div className="mt-3" data-testid={testId}>
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <ul className="mt-1 flex flex-wrap gap-1.5">
        {tables.map((t, i) => (
          <li
            key={`${formatQualifiedName(t)}-${i}`}
            className="rounded-md border border-border bg-muted/40 px-2 py-0.5 font-mono text-xs"
          >
            {formatQualifiedName(t)}
          </li>
        ))}
      </ul>
    </div>
  );
}
